import { useContext, useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router'
import { AuthContext } from '../context/auth-context'
import GlobalToast from '@/components/ui/global-toast/GlobalToast'
import OfflineIndicatorToast from '@/components/ui/global-toast/OfflineIndicatorToast'
import ClipboardAnalyticsListener from '@/analytics/components/ClipboardAnalyticsListener'
import { OAuthProvider, getAuth, getRedirectResult, signInAnonymously, signInWithCredential } from 'firebase/auth'
import AuthDialog from '@/components/auth/AuthDialog'
import { FirebaseError } from 'firebase/app'
import { OnlineStatusProvider } from '@/context/online-status-context'
import PWAUpdateModal from '@/components/modals/PWAUpdateModal'
import SessionEndedModal from '@/components/modals/SessionEndedModal'
import { Userpilot } from 'userpilot'
import { usePollWhileVisible } from '@/hooks/usePollWhileVisible'

/**
 * Root component
 * Global style wrappers and redirects
 * @returns
 */
export default function Root() {
  const authContext = useContext(AuthContext)
  const user = authContext?.user
  const navigate = useNavigate()
  const location = useLocation()
  const [lastAuthRefreshTimestamp, setLastAuthRefreshTimestamp] = useState<number>(Date.now())
  const authRefreshNavigationInterval = 5 * 60 * 1000 // 5 minutes in milliseconds
  const authRefreshBackgroundInterval = 30 * 60 * 1000 // 30 minutes in milliseconds
  const authRefreshVisibleInterval = 2 * 60 * 1000 // 2 minutes; also catches up on tab re-focus

  // Auth
  const auth = getAuth()

  // Refresh account data on tab re-focus and on a short interval while the tab is visible, so an
  // org that expires mid-session (org has_access -> false) is reflected promptly without a manual
  // reload. SubscriptionGuard then routes the member to OrgAccessDeniedPage. Only for permanent
  // (logged-in, non-anonymous) users.
  const permanentUser = user != null && !user.isAnonymous
  usePollWhileVisible(
    () => {
      void authContext?.refreshUserAccountData()
    },
    authRefreshVisibleInterval,
    permanentUser
  )

  // Navigation Observer
  useEffect(() => {
    Userpilot.reload()
  }, [location.pathname]) // re-fire when pathname changes

  // Automate Refresh of AuthContext
  useEffect(() => {
    const debounceRefreshUserAccountData = async () => {
      // Only refresh auth if the user is logged in and not anonymous
      if (auth.currentUser && user != null && !user.isAnonymous) {
        const shouldRefreshAuth = lastAuthRefreshTimestamp < Date.now() - authRefreshNavigationInterval ? true : false
        console.debug('debounceRefreshUserAccountData', lastAuthRefreshTimestamp, shouldRefreshAuth)

        if (shouldRefreshAuth) {
          setLastAuthRefreshTimestamp(Date.now())
          await authContext?.refreshUserAccountData()
        }
      }
    }

    // Run the refresh auth on initial load and every navigation, debounce it tracking the last refresh timestamp
    debounceRefreshUserAccountData()

    // Set up interval to run the action
    const intervalId = setInterval(debounceRefreshUserAccountData, authRefreshBackgroundInterval)

    // Clear the interval on component unmount
    return () => clearInterval(intervalId)
  }, [location.pathname, lastAuthRefreshTimestamp]) // re-fire when pathname changes, add lastAuthRefreshTimestamp dependency to trigger on auth refresh

  // Startup Authentication Handler
  //  1. Checks if we're part of an existing social authentication redirect flow and handles appropriately
  //    a. Attempts to getRedirectResult  (this also auto-handles linking the credential to the anonymous account (see notes below in the error))
  //    b. ERRORS: If we receive error "auth/credential-already-in-use" - there is already a Google Account we can sign in to.
  //
  //  2. Anon Sign In: If redirectResult == null && existingUser == null, sign in anonymously
  //
  useEffect(() => {
    const startupAuthFlow = async () => {
      // Get the existing user for comparison
      const existingUser = auth.currentUser

      try {
        // Check for redirect result
        // Automatically links anonymous account to the redirect result account if new to system
        const redirectResult = await getRedirectResult(auth)

        // If result is null, there was no social redirect
        if (redirectResult == null && existingUser == null) {
          // console.log('No existing user and no social login redirect detected. Logging in anonymously.')

          signInAnonymously(auth)
          console.log('New anonymous user')
          return
        }
      } catch (e) {
        if (e instanceof FirebaseError) {
          // OAuth SIGN IN FLOW:
          // If the getRedirectResult credential is already in use, it means there is an
          // existing Google account we can sign in to. No linking will take place.
          //
          // Handling this in the exception and using credentialFromError somehow appears to be
          // normal handling of getRedirectResult() for linking cases because
          // getRedirectResult seems to automatically attempt linking
          //
          if (e.code == 'auth/credential-already-in-use' || e.code == 'auth/email-already-in-use') {
            console.log('Credential exists in system. Signing in with OAuth:', e)

            // Get the OAuth Credential from the error, supports Google and Generic OAuth
            const errorCredential = OAuthProvider.credentialFromError(e)
            if (errorCredential == null) {
              throw new Error('Cannot sign in existing OAuth account with a null credential')
            }

            await signInWithCredential(auth, errorCredential)
            return
          }
        }
        console.error('Error handling startupAuthFlow redirect result: ', e)
        throw e
      }
    }

    // Run the auth startup flow
    startupAuthFlow()
  }, [user])

  // Root redirect
  useEffect(() => {
    // console.log('Root redirect checking path: ', location.pathname)

    if (location.pathname == '/') {
      console.log("Redirecting from '/' root")
      navigate('/dashboard')
    }

    // Old path redirects
    if (location.pathname == '/dashboard/cfr') {
      navigate('/dashboard')
    }
  })

  return (
    <>
      <AuthDialog />
      <GlobalToast />
      <OnlineStatusProvider>
        <ClipboardAnalyticsListener />
        <OfflineIndicatorToast />
        <Outlet />
      </OnlineStatusProvider>
      <PWAUpdateModal />
      <SessionEndedModal />
    </>
  )
}
